import { type CSSProperties, useEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import classNames from 'classnames';

import { DummyAvatar } from '@/ui/icons/DummyAvatar';
import { getRemainder } from '@/utils/getRemainder';
import { hashToNumber } from '@/utils/hashToNumber';

import MarkDownHelperText from './components/MarkDownHelperText';

import * as styles from './PostCommentForm.css';
import type { PostCommentFormElementProps } from './PostCommentForm.types';

const ALLOWED_CHARACTERS = 1000;

export function PostCommentFormElement(props: PostCommentFormElementProps) {
  const {
    isSubmitting,
    isLive = false,
    element,
    attributes,
    onCancel,
    onCommentChange,
    children,
    isVisitorLoggedIn,
    commenterId,
    onClickLogin,
    onClickSubscribe,
    translations,
    showCancelButton = true,
  } = props;

  const {
    id,
    cancelButtonText,
    submitButtonText,
    submittingButtonText,
    placeholderText,
    textareaBorderColor,
    textareaBorderWidth,
    textareaBorderRadius,
    textareaBorderStyle,
    textareaTextColor,
    textareaPlaceholderColor,
    textareaPadding,
    submitButtonTextColor,
    submitButtonBackgroundColor,
    cancelButtonTextColor,
    cancelButtonBackgroundColor,
    textColor,
  } = element.attrs || {};

  const [content, setContent] = useState<string>('');
  const charCount = content.length || 0;
  const charactersRemaining = ALLOWED_CHARACTERS - charCount;
  const isOverLimit = charactersRemaining < 0;
  const idKey = `comment_${id}`;

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const textAreaComment = textAreaRef.current;

    const onInput = (event: Event) => {
      const textarea = event.target as HTMLTextAreaElement;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    if (textAreaComment) {
      textAreaComment.style.height = 'auto';
      textAreaComment.style.height = `${textAreaComment.scrollHeight}px`;
      textAreaComment.style.overflowY = 'hidden';
      textAreaComment.addEventListener('input', onInput, false);
    }

    return () => {
      // Cleanup event listener if needed
      if (textAreaComment) {
        textAreaComment.removeEventListener('input', onInput, false);
      }
    };
  }, [textAreaRef]);

  const avatarIndex = typeof commenterId === 'string' ? hashToNumber(commenterId) : getRemainder(commenterId || 0);

  return (
    <>
      <div
        className={classnames('flex flex-col gap-y-2 relative w-full', !isLive && 'pointer-events-none')}
        style={
          {
            '--textarea-placeholder-color': textareaPlaceholderColor,
          } as CSSProperties
        }
        {...attributes}
      >
        <div className="flex flex-row w-full gap-x-3">
          <div className={classNames('h-8 w-8 rounded-full object-cover grow-1 shrink-0', styles.authorBorder)}>
            <DummyAvatar index={avatarIndex} className="h-full w-full rounded-full" />
          </div>
          <div className="flex flex-col w-full gap-y-2">
            <div
              className={classnames(
                'w-full relative overflow-hidden focus-within:ring-1',
                isOverLimit
                  ? 'focus-within:border-red-600 focus-within:ring-red-600'
                  : 'focus-within:border-gray-200 focus-within:ring-gray-200',
              )}
              style={{
                borderStyle: textareaBorderStyle,
                borderColor: textareaBorderColor,
                borderWidth: textareaBorderWidth,
                borderRadius: textareaBorderRadius,
                ...(isOverLimit
                  ? {
                      boxShadow: '0px 0px 0px 4px #FECACA, 0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                    }
                  : {}),
              }}
            >
              <textarea
                ref={textAreaRef}
                id={idKey}
                className={classnames(
                  styles.textarea,
                  'text-sm block w-full h-full min-h-[92px] resize-none border-0 bg-transparent focus:ring-0',
                )}
                style={
                  {
                    padding: textareaPadding,
                    color: textareaTextColor,
                  } as CSSProperties
                }
                placeholder={placeholderText}
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                  onCommentChange?.(e.target.value);
                }}
              />
              <div className="absolute right-2 top-1">
                <MarkDownHelperText isLive={isLive} />
              </div>
            </div>
            <div
              className={classnames(
                'flex flex-row items-center',
                isVisitorLoggedIn ? 'justify-end' : 'justify-between',
              )}
            >
              {(!isVisitorLoggedIn || !isLive) && (
                <p
                  className="text-xs"
                  style={
                    {
                      color: textColor,
                    } as CSSProperties
                  }
                >
                  <button type="button" onClick={onClickLogin} className="underline font-semibold">
                    {translations?.['shared.cta.login']}
                  </button>{' '}
                  {translations?.['shared.cta.or']}{' '}
                  <button type="button" onClick={onClickSubscribe} className="underline font-semibold">
                    {translations?.['shared.cta.subscribe']}
                  </button>{' '}
                  {translations?.['shared.cta.to_participate']}
                </p>
              )}
              {(isVisitorLoggedIn || !isLive) && (
                <div className="flex flex-row items-center gap-x-2">
                  {(showCancelButton || !isLive) && (
                    <button
                      type="button"
                      onClick={() => {
                        setContent('');
                        onCommentChange?.('');
                        onCancel?.();
                      }}
                      disabled={isOverLimit || isSubmitting}
                      className={classnames(
                        styles.button,
                        charCount === 0 && isLive && 'hidden',
                        'px-[9px] py-[5px] text-sm rounded-md shadow-[0px_1px_2px_0px_rgba(0, 0, 0, 0.05)]',
                      )}
                      style={{
                        background: cancelButtonBackgroundColor,
                        color: cancelButtonTextColor,
                      }}
                    >
                      {cancelButtonText}
                    </button>
                  )}
                  <button
                    type="submit"
                    onClick={() => {
                      setContent('');
                    }}
                    disabled={isOverLimit || isSubmitting}
                    className={classnames(
                      styles.button,
                      charCount === 0 && isLive && 'hidden',
                      'px-[9px] py-[5px] text-sm rounded-md shadow-[0px_1px_2px_0px_rgba(0, 0, 0, 0.05)]',
                    )}
                    style={{
                      background: submitButtonBackgroundColor,
                      color: submitButtonTextColor,
                    }}
                  >
                    {isSubmitting ? submittingButtonText : submitButtonText}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {children}
    </>
  );
}
